import React, { useState } from 'react'; // Add useState import
import { Play, Check, ArrowRight, Film, Zap, Cloud, Shield, Users, Star } from 'lucide-react';
import { FlagLanguageSwitcher } from './LanguageSwitcher';
import { useLanguage } from '../contexts/LanguageContext';

// Social Media Icons Components
const TelegramIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
  </svg>
);

const GitHubIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
  </svg>
);

const LandingPage = ({ onGetStarted }) => {
  const { t } = useLanguage();

  const features = [
    {
      icon: <Film className="w-8 h-8 text-red-500" />,
      title: t('3dAnimationRendering'),
      description: t('professionalBlenderRendering')
    },
    {
      icon: <Zap className="w-8 h-8 text-yellow-500" />,
      title: t('lightningFast'),
      description: t('powerfulGpuClusters')
    },
    {
      icon: <Cloud className="w-8 h-8 text-blue-500" />,
      title: t('cloudBased'),
      description: t('noExpensiveHardware')
    },
    {
      icon: <Shield className="w-8 h-8 text-green-500" />,
      title: t('secureReliable'),
      description: t('filesEncryptedSecure')
    }
  ];

  const plans = [
    {
      name: t('free'),
      price: "0",
      period: t('forever'),
      features: [t('threeRendersPerMonth'), t('1080pResolution'), t('standardQueue'), t('emailSupport')],
      cta: t('startFree'),
      popular: false
    },
    {
      name: t('pro'),
      price: "25",
      period: t('month'),
      features: [t('twintyRendersPerMonth'), t('fourKResolution'), t('priorityQueue'), t('liveChatSupport')],
      cta: t('goPro'),
      popular: true
    },
    {
      name: t('studio'),
      price: "50", 
      period: t('month'),
      features: [t('thirtyfiveRendersPerMonth'), t('fourKResolution'), t('expressQueue'), t('dedicatedSupport')],
      cta: t('getStudio'),
      popular: false
    }
  ];

  const testimonials = [
    {
      name: "Sara Chen",
      role: t('documentaryCreator'),
      content: t('testimonial1'),
      avatar: "SC"
    },
    {
      name: "Mike Rodriguez", 
      role: t('3dArtist'),
      content: t('testimonial2'),
      avatar: "MR"
    },
    {
      name: "Emma Thompson",
      role: t('contentCreator'), 
      content: t('testimonial3'),
      avatar: "ET"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white">
      {/* Header - FIXED MOBILE NAVIGATION */}
      <header className="relative z-10 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center">
              <Film className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold">intCount</h1>
              <p className="text-sm text-gray-400">{t('renderStudio')}</p>
            </div>
          </div>
          
          {/* Header Actions - MOBILE FRIENDLY */}
          <div className="flex items-center space-x-2 md:space-x-4">
            {/* Social Links - VISIBLE ON MOBILE TOO */}
            <div className="flex items-center space-x-1 md:space-x-2">
              <a 
                href="https://t.me/intcounters"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-blue-400 p-2 rounded-lg hover:bg-gray-800/50 transition-colors"
                title={t('joinTelegram')}
              >
                <TelegramIcon className="w-4 h-4 md:w-5 md:h-5" />
              </a>
              
              <a 
                href="https://github.com/intcount"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white p-2 rounded-lg hover:bg-gray-800/50 transition-colors"
                title={t('viewOnGitHub')}
              >
                <GitHubIcon className="w-4 h-4 md:w-5 md:h-5" />
              </a>
            </div>

            <FlagLanguageSwitcher className="hidden sm:block" />
            
            <button 
              onClick={onGetStarted}
              className="bg-red-600 hover:bg-red-700 text-white px-4 md:px-6 py-2 rounded-lg font-medium transition-colors text-sm md:text-base"
            >
              {t('signIn')}
            </button>
          </div>
        </div>

        {/* Mobile Language Switcher - Only on small screens */}
        <div className="sm:hidden mt-4 flex justify-center">
          <FlagLanguageSwitcher />
        </div>
      </header>

      {/* Hero Section */}
      <section className="px-6 py-20">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Hero Text */}
            <div className="space-y-8">
              <div className="space-y-4">
                <h1 className="text-5xl lg:text-6xl font-bold leading-tight">
                  {t('professional')}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-500">
                    {' '}{t('3dRendering')}
                  </span>
                  <br />
                  {t('inTheCloud')}
                </h1>
                <p className="text-xl text-gray-300 leading-relaxed">
                  {t('heroDescription')}
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <button 
                  onClick={onGetStarted}
                  className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white px-8 py-4 rounded-xl font-semibold text-lg transition-all transform hover:scale-105 flex items-center justify-center"
                >
                  {t('startRenderingFree')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </button>
                
                <button className="border border-gray-600 hover:border-gray-500 text-white px-8 py-4 rounded-xl font-semibold text-lg transition-colors flex items-center justify-center">
                  <Play className="mr-2 w-5 h-5" />
                  {t('watchDemo')}
                </button>
              </div>

              <div className="flex items-center space-x-8 text-sm">
                <div className="flex items-center space-x-2">
                  <Check className="w-5 h-5 text-green-500" />
                  <span>{t('noCreditCardRequired')}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Check className="w-5 h-5 text-green-500" />
                  <span>{t('3FreeRenders')}</span>
                </div>
              </div>
            </div>

            {/* Hero Visual */}
            <div className="relative">
              <div className="relative bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 border border-gray-700">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                      <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                      <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    </div>
                    <div className="text-sm text-gray-400">render_project.blend</div>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="bg-gray-700 rounded-lg p-4">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm">{t('ancientRomeScene')}</span>
                        <span className="text-green-400 text-sm">{t('complete')}</span>
                      </div>
                      <div className="w-full bg-gray-600 rounded-full h-2">
                        <div className="bg-green-500 h-2 rounded-full w-full"></div>
                      </div>
                    </div>
                    
                    <div className="bg-gray-700 rounded-lg p-4">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm">{t('dnaVisualization')}</span>
                        <span className="text-yellow-400 text-sm">{t('rendering67Percent')}</span>
                      </div>
                      <div className="w-full bg-gray-600 rounded-full h-2">
                        <div className="bg-yellow-500 h-2 rounded-full w-2/3"></div>
                      </div>
                    </div>
                    
                    <div className="bg-gray-700 rounded-lg p-4">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm">{t('solarSystemTour')}</span>
                        <span className="text-blue-400 text-sm">{t('queued')}</span>
                      </div>
                      <div className="w-full bg-gray-600 rounded-full h-2">
                        <div className="bg-blue-500 h-2 rounded-full w-1/4"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="px-6 py-20 bg-gray-900/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">{t('whyChooseRenderStudio')}</h2>
            <p className="text-xl text-gray-400">{t('professionalToolsDescription')}</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-gray-800/50 backdrop-blur rounded-xl p-6 border border-gray-700 hover:border-gray-600 transition-colors">
                <div className="mb-4">{feature.icon}</div>
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-gray-400">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="px-6 py-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">{t('howItWorks')}</h2>
            <p className="text-xl text-gray-400">{t('threeSimpleSteps')}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold">1</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">{t('uploadYourBlenderFile')}</h3>
              <p className="text-gray-400">{t('dragDropBlendFile')}</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold">2</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">{t('configureSettings')}</h3>
              <p className="text-gray-400">{t('chooseResolutionQuality')}</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold">3</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">{t('downloadResults')}</h3>
              <p className="text-gray-400">{t('getNotifiedWhenComplete')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="px-6 py-20 bg-gray-900/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">{t('simpleTransparentPricing')}</h2>
            <p className="text-xl text-gray-400">{t('startFreeUpgrade')}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {plans.map((plan, index) => (
              <div key={index} className={`relative bg-gray-800 rounded-2xl p-8 border ${plan.popular ? 'border-red-500 scale-105' : 'border-gray-700'}`}>
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-red-500 text-white px-4 py-1 rounded-full text-sm font-medium">{t('mostPopular')}</span>
                  </div>
                )}
                
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                  <div className="mb-4">
                    <span className="text-4xl font-bold">${plan.price}</span>
                    <span className="text-gray-400">/{plan.period}</span>
                  </div>
                </div>

                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center">
                      <Check className="w-5 h-5 text-green-500 mr-3" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <button 
                  onClick={onGetStarted}
                  className={`w-full py-3 rounded-lg font-semibold transition-colors ${
                    plan.popular 
                      ? 'bg-red-500 hover:bg-red-600 text-white' 
                      : 'bg-gray-700 hover:bg-gray-600 text-white'
                  }`}
                >
                  {plan.cta}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="px-6 py-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">{t('lovedByCreators')}</h2>
            <p className="text-xl text-gray-400">{t('joinThousandsSatisfied')}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-gray-800 rounded-xl p-6 border border-gray-700">
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-500 fill-current" />
                  ))}
                </div>
                <p className="text-gray-300 mb-6">"{testimonial.content}"</p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center mr-4">
                    <span className="font-bold">{testimonial.avatar}</span>
                  </div>
                  <div>
                    <h4 className="font-semibold">{testimonial.name}</h4>
                    <p className="text-gray-400 text-sm">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-6 py-20 bg-gradient-to-r from-red-600 to-red-700">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-4">{t('readyToAccelerate')}</h2>
          <p className="text-xl mb-8 text-red-100">{t('joinThousandsCreators')}</p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <button 
              onClick={onGetStarted}
              className="bg-white text-red-600 hover:bg-gray-100 px-8 py-4 rounded-xl font-semibold text-lg transition-colors flex items-center"
            >
              {t('startYourFreeTrial')}
              <ArrowRight className="ml-2 w-5 h-5" />
            </button>
            
            <div className="flex items-center space-x-2 text-red-100">
              <Users className="w-5 h-5" />
              <span>{t('joinTenThousandCreators')}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer with CORRECTED Social Links */}
      <footer className="px-6 py-12 bg-black border-t border-gray-800">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            {/* Company Info */}
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 rounded-lg bg-red-600 flex items-center justify-center">
                  <Film className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-bold">intCount Render Studio</h3>
                  <p className="text-sm text-gray-400">
                    {t('professionalRenderingCloud')}
                  </p>
                </div>
              </div>
              <p className="text-gray-400 text-sm">
                {t('transformCreativeVision')}
              </p>
            </div>

            {/* Connect With Us - FIXED URLs */}
            <div className="space-y-4">
              <h4 className="font-semibold text-white">
                {t('connectWithUs')}
              </h4>
              <div className="flex flex-col space-y-2 sm:flex-row sm:space-y-0 sm:space-x-4">
                <a 
                  href="https://t.me/intcounters"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-2 text-gray-400 hover:text-blue-400 transition-colors p-2 rounded-lg hover:bg-gray-900/50"
                  title={t('joinTelegram')}
                >
                  <TelegramIcon className="w-5 h-5" />
                  <span className="text-sm">Telegram</span>
                </a>
                
                <a 
                  href="https://github.com/intcount"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors p-2 rounded-lg hover:bg-gray-900/50"
                  title={t('viewOnGitHub')}
                >
                  <GitHubIcon className="w-5 h-5" />
                  <span className="text-sm">GitHub</span>
                </a>
              </div>
              
              <p className="text-gray-500 text-xs">
                {t('joinCommunityForUpdates')}
              </p>
            </div>
          </div>
          
          <div className="pt-8 border-t border-gray-800 flex flex-col md:flex-row justify-between items-center">
            <div className="text-gray-400 text-sm mb-4 md:mb-0">
              © 2025 intCount. {t('allRightsReserved')}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;